// Pure model for the Calendar tab: a multi-week sprint, the daily routine that runs
// beside it, and a nightly scorecard.
//
// Two rules shape this file, both learned from the sync bugs documented in
// cloudSync.js and goalPlanSync.js:
//
// 1. normalize(x) must equal normalize(normalize(x)). Ids missing from the input are
//    derived from position, never generated, and map keys come out sorted.
// 2. Only an explicit import writes the board at its bare field name. updateDoc
//    REPLACES a map written that way, so a phone holding an older copy would flatten
//    every other device's edits. Ordinary edits go out as the dotted paths
//    diffSprintBoard builds: one per section, one per date.

import { SPRINT_BOARD_FIELD, applyDottedPatch } from './cloudSync.js';

const SPRINT_PATH_PREFIX = `${SPRINT_BOARD_FIELD}.`;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

export const SPRINT_TONES = ['', 'orange', 'blue', 'green'];
export const SCORE_FIELD_TYPES = ['number', 'yesno'];
export const SPRINT_VERDICTS = ['win', 'loss'];
export const SPRINT_DEFAULT_WEEKS = 4;
export const SPRINT_MAX_WEEKS = 8;

const pad2 = (n) => String(n).padStart(2, '0');
const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const asList = (v) => (Array.isArray(v) ? v : []);
const isRecord = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v, max) => (typeof v === 'string' ? v : '').slice(0, max);
const cleanKey = (v) => (typeof v === 'string' || typeof v === 'number'
  ? String(v).replace(/[^A-Za-z0-9_-]/g, '')
  : '');

// --- DATES (UTC math on YYYY-MM-DD strings, so no timezone or DST drift) ---

const isoFromMs = (ms) => new Date(ms).toISOString().slice(0, 10);

export const isIsoDate = (v) => {
  if (typeof v !== 'string' || !ISO_DATE.test(v)) return false;
  const ms = Date.parse(`${v}T00:00:00Z`);
  // Round-trip, because some engines roll 2026-02-31 forward instead of rejecting it.
  return Number.isFinite(ms) && isoFromMs(ms) === v;
};

export const addDays = (iso, n) => isoFromMs(Date.parse(`${iso}T00:00:00Z`) + n * DAY_MS);

export const mondayOf = (iso) => {
  const dow = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return addDays(iso, dow === 0 ? -6 : 1 - dow);
};

// The device's own calendar date. The rest of the app derives "today" from
// toISOString(), which is UTC and therefore still yesterday until 08:00 in Singapore.
export const localDateKey = (d = new Date()) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// --- IDS ---

let idSeq = 0;
export const makeSprintId = (prefix = 'i') =>
  `${prefix}${Date.now().toString(36)}${(idSeq++).toString(36)}${Math.random().toString(36).slice(2, 5)}`;

// Every item leaves with a unique, path-safe id. A missing or duplicate id is
// replaced by one derived from its position, which a second pass leaves alone.
const withStableIds = (items, prefix) => {
  const seen = new Set();
  return items.map((item, i) => {
    let id = cleanKey(item.id);
    if (!id || seen.has(id)) id = `${prefix}${i}`;
    while (seen.has(id)) id = `${id}_`;
    seen.add(id);
    return { ...item, id };
  });
};

// --- NORMALIZERS ---

const cleanTone = (v) => (SPRINT_TONES.includes(v) ? v : '');

const cleanClock = (v) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(typeof v === 'string' ? v.trim() : '');
  if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) return '';
  return `${pad2(Number(m[1]))}:${m[2]}`;
};

const normalizeRoutine = (input) => withStableIds(
  asList(input).filter(isRecord).slice(0, 60).map((r) => ({
    id: r.id,
    start: cleanClock(r.start),
    end: cleanClock(r.end),
    title: text(r.title, 80),
    note: text(r.note, 240),
    tone: cleanTone(r.tone)
  })),
  'r'
);

const normalizeTasks = (input) => withStableIds(
  asList(input).filter(isRecord).slice(0, 30).map((t) => ({
    id: t.id,
    text: text(t.text, 240),
    done: t.done === true,
    key: t.key === true
  })),
  't'
);

const normalizeScoreFields = (input) => withStableIds(
  asList(input).filter(isRecord).slice(0, 20).map((f) => ({
    id: f.id,
    label: text(f.label, 60),
    type: SCORE_FIELD_TYPES.includes(f.type) ? f.type : 'number'
  })),
  's'
);

const normalizeDateMap = (input, build) => {
  const src = asObject(input);
  const out = {};
  Object.keys(src).filter(isIsoDate).sort().forEach((date) => {
    if (isRecord(src[date])) out[date] = build(src[date]);
  });
  return out;
};

// An emptied day or score is kept, not dropped: dropping it would leave nothing for
// diffSprintBoard to send, and the cloud copy would bring the old tasks straight back.
const normalizeDay = (d) => ({ tasks: normalizeTasks(d.tasks), mark: d.mark === true });

const normalizeWeek = (w) => ({ title: text(w.title, 80), tone: cleanTone(w.tone) });

const normalizeScore = (s) => {
  const src = asObject(s.values);
  const values = {};
  Object.keys(src).sort().forEach((rawKey) => {
    const key = cleanKey(rawKey);
    const raw = src[rawKey];
    const value = (typeof raw === 'number' && Number.isFinite(raw) ? String(raw) : text(raw, 20)).trim();
    if (key && value) values[key] = value;
  });
  return { values, verdict: SPRINT_VERDICTS.includes(s.verdict) ? s.verdict : '' };
};

export const normalizeSprintBoard = (raw) => {
  const src = asObject(raw);
  const meta = asObject(src.meta);
  const weeks = Math.round(Number(meta.weekCount));
  return {
    meta: {
      title: text(meta.title, 120),
      subtitle: text(meta.subtitle, 300),
      // Empty means "no sprint set up yet" — the tab shows its empty state.
      startDate: isIsoDate(meta.startDate) ? meta.startDate : '',
      weekCount: Number.isFinite(weeks) && weeks >= 1 && weeks <= SPRINT_MAX_WEEKS
        ? weeks
        : SPRINT_DEFAULT_WEEKS,
      footnote: text(meta.footnote, 600)
    },
    routine: normalizeRoutine(src.routine),
    scoreFields: normalizeScoreFields(src.scoreFields),
    weeks: normalizeDateMap(src.weeks, normalizeWeek),
    days: normalizeDateMap(src.days, normalizeDay),
    scores: normalizeDateMap(src.scores, normalizeScore)
  };
};

export const isSprintBoardEmpty = (board) => !board?.meta?.startDate;

// Accepts a bare board or a file that wraps one ({ sprintBoard: {...} }), so a full
// app backup works as a source too. A file with no start date is not a calendar.
export const readSprintBoardImport = (parsed) => {
  const src = asObject(parsed);
  const board = normalizeSprintBoard(
    src[SPRINT_BOARD_FIELD] !== undefined ? src[SPRINT_BOARD_FIELD] : src
  );
  return isSprintBoardEmpty(board) ? null : board;
};

// --- VIEW HELPERS ---

// [{ key: '<first date of the week>', dates: [7 ISO dates] }]
export const sprintWeeks = (board) => {
  const start = board?.meta?.startDate;
  if (!isIsoDate(start)) return [];
  return Array.from({ length: board.meta.weekCount }, (_, w) => {
    const key = addDays(start, w * 7);
    return { key, dates: Array.from({ length: 7 }, (_, i) => addDays(key, i)) };
  });
};

// --- EDITS ---

export const emptySprintDay = () => ({ tasks: [], mark: false });
export const emptySprintScore = () => ({ values: {}, verdict: '' });
export const emptySprintWeek = () => ({ title: '', tone: '' });

export const updateSprintDay = (board, date, fn) => ({
  ...board,
  days: { ...board.days, [date]: fn(board.days[date] || emptySprintDay()) }
});

export const updateSprintScore = (board, date, fn) => ({
  ...board,
  scores: { ...board.scores, [date]: fn(board.scores[date] || emptySprintScore()) }
});

export const updateSprintWeek = (board, weekKey, fn) => ({
  ...board,
  weeks: { ...board.weeks, [weekKey]: fn(board.weeks[weekKey] || emptySprintWeek()) }
});

export const moveListItem = (list, index, delta) => {
  const to = index + delta;
  if (index < 0 || to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(to, 0, item);
  return next;
};

// --- CLOUD PATCH ---

const SECTION_KEYS = ['meta', 'routine', 'scoreFields'];
const DATE_MAP_KEYS = ['weeks', 'days', 'scores'];
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Both boards must come from normalizeSprintBoard. Only run this on a user edit,
// never inside the snapshot handler: it has no cloud baseline to settle against.
// Dates are never removed by an edit — they are emptied in place — so there is no
// delete path here to get wrong.
export const diffSprintBoard = (prev, next) => {
  const patch = {};
  SECTION_KEYS.forEach((key) => {
    if (!same(prev?.[key], next[key])) patch[`${SPRINT_BOARD_FIELD}.${key}`] = next[key];
  });
  DATE_MAP_KEYS.forEach((key) => {
    Object.entries(next[key]).forEach(([date, value]) => {
      if (!same(prev?.[key]?.[date], value)) {
        patch[`${SPRINT_BOARD_FIELD}.${key}.${date}`] = value;
      }
    });
  });
  return patch;
};

// --- PENDING QUEUE ---

export const hasWholeSprintBoard = (updates) =>
  isRecord(updates) && updates[SPRINT_BOARD_FIELD] !== undefined;

// The pending queue coalesces into one patch. A parked import (bare field) merged with
// a parked edit (dotted path) would hand updateDoc a field together with its own
// prefix; Firestore rejects that patch whole, and a rejected patch is re-queued
// forever, blocking every later save. The whole board is always the newer of the two
// — the app sends whole boards for as long as one is parked — so the paths go.
export const dropShadowedSprintPaths = (updates) => {
  if (!hasWholeSprintBoard(updates)) return updates;
  const out = {};
  Object.keys(updates).forEach((key) => {
    if (!key.startsWith(SPRINT_PATH_PREFIX)) out[key] = updates[key];
  });
  return out;
};

// Edits still parked in the queue have not reached the server, so a snapshot would
// show the board without them until the queue drains. Display only: nothing here is
// written back, so it cannot feed a sync loop.
export const applySprintBoardPatches = (board, patches) => {
  let doc = { [SPRINT_BOARD_FIELD]: board };
  asList(patches).forEach((patch) => {
    const own = {};
    Object.keys(asObject(patch)).forEach((key) => {
      if (key === SPRINT_BOARD_FIELD || key.startsWith(SPRINT_PATH_PREFIX)) own[key] = patch[key];
    });
    doc = applyDottedPatch(doc, own);
  });
  return normalizeSprintBoard(doc[SPRINT_BOARD_FIELD]);
};
